import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader, ErrorFallback, TextInput } from 'smart-react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import {
  buildNotification,
  GenerateNotification,
  isValidate,
  isFromValid,
  DescriptionTextArea,
  InputSwitch,
} from 'smart-react';
import { createProduct, updateProduct } from '../Services/ProductService';
import {
  base64ToObject,
  objectToBase64,
} from '../../../../Utils/Common/commonUtils';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { Error } from '@progress/kendo-react-labels';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Core/Context/AuthContext';
import { isAdminRole } from '../../../../Utils/Auth/ValidatePermissions';
import NoPermission from '../../../Core/NoPermission/NoPermission';
import DesktopLayout from '../Components/Layout/DesktopLayout';
import { getProduct } from '../Services/ProductService';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add New Product main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Product component.
 */
const AddProduct = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Get tenantID using Auth Context
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  /**
   * Used to handle multiple tabs
   */
  const [selected, setSelected] = React.useState(0);
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [isTextArea, setIsTextArea] = React.useState(false);
  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [noRecord, setNoRecord] = React.useState(false);
  const [formData, setFormData] = React.useState({
    product_id: '',
    name: '',
    display_name: '',
    description: '',
    active: true,
  });
  const [validateForm, setValidateForm] = React.useState(false);
  const requiredFields = ['name', 'description'];
  const [editorField, setEditorField] = React.useState({
    name: 'description',
    value: formData?.description,
    label: 'Description',
  });
  const { state } = useLocation();

  const [tempState, setTempState] = React.useState(state);

  /**
   * Handle Selected Tab
   * @param {*} e
   */
  const handleSelect = (e) => {
    setSelected(e.selected);
    setShouldUpdate(!shouldUpdate);
  };

  /**
   * Get Product Detail from API for edit Product
   * @param {*} stateData
   */
  const fetchProductData = async (stateData) => {
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await getProduct({
        data: refObj,
        isServiceWorker: false,
      });
      const responseData = response.Payload;
      if (responseData) {
        setTempState({ ...stateData, dataSet: responseData, edit: true });
        setFormData(responseData);
        setNoRecord(false);
        setDetailsDataModified(true);
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (tempState || (refId && refId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchProductData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setDetailsDataModified(true);
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * onSave
   * submit the Product data to api
   */
  const onSave = async ({ isExit = false, isContinue = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
    };
    let response;
    let method;

    if (tempState?.edit) {
      method = updateProduct;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createProduct;
      notificationMetaData = CREATE_MESSAGE;
      setFormData({
        product_id: '',
        name: '',
        display_name: '',
        description: '',
        active: true,
      });
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        const responseData = response?.Payload;
        // Update tempState to trigger rerender
        setTempState({
          ...tempState,
          dataSet: responseData,
        });
        if (isContinue) {
          const refId = objectToBase64({
            product_id: responseData?.product_id,
          });
          navigate(`/admin/product?refId=${refId}`, {
            state: {
              edit: true,
              dataSet: responseData,
            },
          });
        }
        if (isExit) {
          navigate('/admin/products');
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to Products listing screen
   */
  const onExit = () => {
    navigate('/admin/products');
  };

  /**
   * handle click on save split button
   */
  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        {!isAdminRole(user) && <NoPermission />}
        {isAdminRole(user) && (
          <React.Suspense fallback={<Loader />}>
            {loading && <Loader />}
            {noRecord ? (
              <NoRecordFound
                recordNotFoundText={'No Data found'}
                onExit={onExit}
              />
            ) : (
              <>
                <div className={`${isTextArea ? '' : 'k-d-none'}`}>
                  <DescriptionTextArea
                    onChange={handleInputChange}
                    label={'Description'}
                    renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                    validateForm={validateForm}
                    formData={formData}
                    setFormData={setFormData}
                    detailsDataModified={detailsDataModified}
                    isTextArea={isTextArea}
                    setIsTextArea={setIsTextArea}
                    editorField={editorField}
                    setEditorField={setEditorField}
                    row={2}
                  />
                </div>
                <div
                  className={`add-rf-profile-group main-page-section add-page-section ${!isTextArea ? '' : 'k-d-none'}`}
                >
                  <Card className='tophead'>
                    <CardHeader>
                      <div className='action-buttons-container'>
                        <Link
                          className='action-icon-wrapper !k-d-block'
                          to={`/admin/products`}
                        >
                          <FontAwesomeIcon
                            className='k-mr-2 k-pt-3'
                            icon={faArrowLeft}
                          />
                        </Link>
                        <div>
                          <p className='display-text k-mb-0'>
                            Product: {formData?.name}
                          </p>
                        </div>
                      </div>
                      {formData?.description && (
                        <div className='desciption-label'>
                          <span className='k-d-block k-mb-2'>
                            {formData?.description}
                          </span>
                        </div>
                      )}
                    </CardHeader>
                  </Card>
                  <Card className='k-mt-3 detail-card'>
                    <CardHeader>
                      <h6 className='card-title-secondary'>Details</h6>
                    </CardHeader>
                    <CardBody className='detail-card-body'>
                      <div>
                        <Form
                          onSubmit={onSave}
                          render={(formRenderProps) => (
                            <div className='card-content'>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.name) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'name'}
                                    id={'name'}
                                    name={'name'}
                                    data={formData?.name}
                                    onChange={handleInputChange}
                                    label={'Name'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(validateForm, formData?.name) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.display_name) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'display_name'}
                                    id={'display_name'}
                                    name={'display_name'}
                                    data={formData?.display_name}
                                    onChange={handleInputChange}
                                    label={'Display Name'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.display_name,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                              </div>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper field-wrapper-description field-wrapper-100 ${isValidate(validateForm, formData?.description) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <DescriptionTextArea
                                    onChange={handleInputChange}
                                    label={'Description'}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    validateForm={validateForm}
                                    formData={formData}
                                    setFormData={setFormData}
                                    detailsDataModified={detailsDataModified}
                                    isTextArea={isTextArea}
                                    setIsTextArea={setIsTextArea}
                                    editorField={{
                                      name: 'description',
                                      value: formData?.description,
                                      label: 'Description',
                                    }}
                                    setEditorField={setEditorField}
                                    requiredField={true}
                                    row={2}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.description,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                              </div>
                              <div className='single-field-row k-d-flex k-mt-4'>
                                <div className={`field-wrapper`}>
                                  <Field
                                    name='active'
                                    component={InputSwitch}
                                    label='Active'
                                    onLabel='Yes'
                                    offLabel='No'
                                    id='active'
                                    checked={formData?.active}
                                    onChange={handleInputChange}
                                    disabled={!detailsDataModified}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </CardBody>
                  </Card>
                  {detailsDataModified && (
                    <Card className='k-mt-3'>
                      <CardHeader>
                        <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                          <>
                            <Button
                              icon={'cancel'}
                              onClick={() => onExit()}
                              type='button'
                            >
                              Cancel
                            </Button>
                            <SplitButton
                              text='save'
                              themeColor={'primary'}
                              onButtonClick={() =>
                                onSave({ isExit: false, isContinue: true })
                              }
                              onItemClick={handleSaveSplit}
                            >
                              <SplitButtonItem text='Save & Close' />
                            </SplitButton>
                          </>
                        </div>
                      </CardHeader>
                    </Card>
                  )}
                  {tempState?.edit && (
                    <Card className='k-mt-3 detail-grid-section'>
                      <CardBody className='!k-px-0'>
                        <DesktopLayout
                          handleSelect={handleSelect}
                          selected={selected}
                          detailsDataModified={detailsDataModified}
                          state={tempState}
                          shouldUpdate={shouldUpdate}
                          setShouldUpdate={setShouldUpdate}
                        />
                      </CardBody>
                    </Card>
                  )}
                </div>
              </>
            )}
          </React.Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};
export default AddProduct;
